import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PencilIcon, ArrowLeftIcon, TrashIcon, LockClosedIcon, ChatBubbleLeftIcon, CheckCircleIcon, XCircleIcon, CubeIcon } from '@heroicons/react/20/solid'
import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import { serviceCasesApi } from '../../lib/api'
import { ServiceCaseForm } from '../../components/forms/ServiceCaseForm'
import { ServiceCaseStatusBadge } from '../../components/ui/ServiceCaseStatusBadge'
import { ServiceCasePriorityBadge } from '../../components/ui/ServiceCasePriorityBadge'
import { ServiceCaseCategoryBadge } from '../../components/ui/ServiceCaseCategoryBadge'
import { Avatar } from '../../components/ui/Avatar'
import type { ServiceCaseInput, ServiceCaseStatus, ServiceCaseCommentInput } from '../../../shared/types'

export function ServiceCaseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [commentBody, setCommentBody] = useState('')
  const [isInternal, setIsInternal] = useState(false)

  const { data: serviceCase, isLoading, error } = useQuery({
    queryKey: ['service-cases', id],
    queryFn: () => serviceCasesApi.get(id!),
    enabled: !!id,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<ServiceCaseInput>) => serviceCasesApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-cases', id] })
      queryClient.invalidateQueries({ queryKey: ['service-cases'] })
      setIsEditing(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => serviceCasesApi.delete(id!),
    onSuccess: () => {
      navigate('/service-cases')
    },
  })

  const resolveMutation = useMutation({
    mutationFn: () => serviceCasesApi.resolve(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-cases', id] })
      queryClient.invalidateQueries({ queryKey: ['service-cases'] })
    },
  })

  const closeMutation = useMutation({
    mutationFn: () => serviceCasesApi.close(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-cases', id] })
      queryClient.invalidateQueries({ queryKey: ['service-cases'] })
    },
  })

  const addCommentMutation = useMutation({
    mutationFn: (data: ServiceCaseCommentInput) => serviceCasesApi.addComment(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-cases', id] })
      setCommentBody('')
      setIsInternal(false)
    },
  })

  const handleStatusChange = (status: ServiceCaseStatus) => {
    updateMutation.mutate({ status })
  }

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentBody.trim()) return
    addCommentMutation.mutate({ body: commentBody, isInternal })
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this service case?')) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) return <div className="p-8">Loading...</div>
  if (error || !serviceCase) return <div className="p-8">Service case not found</div>

  const canResolve = !['resolved', 'closed'].includes(serviceCase.status)
  const canClose = serviceCase.status !== 'closed'

  return (
    <>
      {/* Secondary column (desktop sidebar) */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="mb-6">
          <Link
            to="/service-cases"
            className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Back to Service Cases
          </Link>
        </div>

        <div className="mb-6">
          <div className="flex items-center gap-x-2 mb-2">
            <WrenchScrewdriverIcon className="h-5 w-5 text-gray-400" />
            <span className="text-lg font-bold text-gray-900">{serviceCase.caseNumber}</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <ServiceCaseStatusBadge status={serviceCase.status} />
            <ServiceCasePriorityBadge priority={serviceCase.priority} />
            <ServiceCaseCategoryBadge category={serviceCase.category} />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 mb-6">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
          >
            <PencilIcon className="h-4 w-4" />
            {isEditing ? 'Cancel Edit' : 'Edit'}
          </button>
          {canResolve && (
            <button
              onClick={() => resolveMutation.mutate()}
              disabled={resolveMutation.isPending}
              className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 disabled:opacity-50"
            >
              <CheckCircleIcon className="h-4 w-4" />
              Resolve
            </button>
          )}
          {canClose && (
            <button
              onClick={() => closeMutation.mutate()}
              disabled={closeMutation.isPending}
              className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-600 disabled:opacity-50"
            >
              <XCircleIcon className="h-4 w-4" />
              Close Case
            </button>
          )}
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="w-full inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50"
          >
            <TrashIcon className="h-4 w-4" />
            Delete
          </button>
        </div>

        {/* Quick Info */}
        <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5 mb-6">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Info</h3>
          <dl className="space-y-3">
            <div>
              <dt className="text-xs font-medium text-gray-500">Status</dt>
              <dd className="mt-1">
                <select
                  value={serviceCase.status}
                  onChange={(e) => handleStatusChange(e.target.value as ServiceCaseStatus)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                >
                  {(['open', 'in_progress', 'waiting_customer', 'waiting_parts', 'resolved', 'closed'] as const).map((status) => (
                    <option key={status} value={status}>
                      {status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </option>
                  ))}
                </select>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">Priority</dt>
              <dd className="mt-1">
                <ServiceCasePriorityBadge priority={serviceCase.priority} />
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500">Assigned To</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {serviceCase.assignedTo ? (
                  <div className="flex items-center gap-x-2">
                    <Avatar firstName={serviceCase.assignedTo.firstName ?? ''} lastName={serviceCase.assignedTo.lastName ?? ''} size="xs" />
                    <span>{serviceCase.assignedTo.firstName} {serviceCase.assignedTo.lastName}</span>
                  </div>
                ) : (
                  <span className="text-gray-400">Unassigned</span>
                )}
              </dd>
            </div>
            {serviceCase.dueDate && (
              <div>
                <dt className="text-xs font-medium text-gray-500">Due Date</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {new Date(serviceCase.dueDate).toLocaleDateString()}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(serviceCase.createdAt).toLocaleDateString()}
              </dd>
            </div>
            {serviceCase.createdBy && (
              <div>
                <dt className="text-xs font-medium text-gray-500">Created By</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <div className="flex items-center gap-x-2">
                    <Avatar firstName={serviceCase.createdBy.firstName ?? ''} lastName={serviceCase.createdBy.lastName ?? ''} size="xs" />
                    <span>{serviceCase.createdBy.firstName} {serviceCase.createdBy.lastName}</span>
                  </div>
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Related Records */}
        <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Related Records</h3>
          <dl className="space-y-3">
            {serviceCase.asset && (
              <div>
                <dt className="text-xs font-medium text-gray-500">Asset</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <Link to={`/assets/${serviceCase.assetId}`} className="text-brand-600 hover:text-brand-800 flex items-center gap-2">
                    <CubeIcon className="h-4 w-4" />
                    {serviceCase.asset.serialNumber}
                  </Link>
                </dd>
              </div>
            )}
            {serviceCase.account && (
              <div>
                <dt className="text-xs font-medium text-gray-500">Account</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <Link to={`/accounts/${serviceCase.accountId}`} className="text-brand-600 hover:text-brand-800">
                    {serviceCase.account.name}
                  </Link>
                </dd>
              </div>
            )}
            {serviceCase.relatedPartNumber && (
              <div>
                <dt className="text-xs font-medium text-gray-500">Related Part</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{serviceCase.relatedPartNumber}</dd>
              </div>
            )}
          </dl>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <Link
              to="/service-cases"
              className="inline-flex items-center gap-x-2 text-sm font-semibold hover:opacity-75 text-brand-500 mb-4"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              Back to Service Cases
            </Link>
            <div className="flex items-center gap-x-2 mb-2">
              <WrenchScrewdriverIcon className="h-5 w-5 text-gray-400" />
              <span className="font-bold text-gray-900">{serviceCase.caseNumber}</span>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <ServiceCaseStatusBadge status={serviceCase.status} />
              <ServiceCasePriorityBadge priority={serviceCase.priority} />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className="inline-flex items-center gap-x-2 rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
              >
                <PencilIcon className="h-4 w-4" />
                {isEditing ? 'Cancel' : 'Edit'}
              </button>
              {canResolve && (
                <button
                  onClick={() => resolveMutation.mutate()}
                  className="inline-flex items-center gap-x-2 rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500"
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  Resolve
                </button>
              )}
            </div>
          </div>

          {isEditing ? (
            <div>
              <h2 className="text-lg font-medium mb-4">Edit Service Case</h2>
              <div className="max-w-2xl">
                <ServiceCaseForm
                  defaultValues={{
                    assetId: serviceCase.assetId,
                    accountId: serviceCase.accountId,
                    contactId: serviceCase.contactId,
                    subject: serviceCase.subject,
                    description: serviceCase.description,
                    status: serviceCase.status,
                    priority: serviceCase.priority,
                    category: serviceCase.category,
                    channel: serviceCase.channel,
                    assignedToId: serviceCase.assignedToId,
                    dueDate: serviceCase.dueDate ? new Date(serviceCase.dueDate).toISOString().slice(0, 16) : undefined,
                    relatedPartNumber: serviceCase.relatedPartNumber,
                  }}
                  onSubmit={(data) => updateMutation.mutate(data)}
                  onCancel={() => setIsEditing(false)}
                  loading={updateMutation.isPending}
                  fixedAssetId={serviceCase.assetId}
                />
              </div>
            </div>
          ) : (
            <>
              {/* Subject */}
              <div className="mb-6">
                <h1 className="text-xl font-semibold text-gray-900">{serviceCase.subject}</h1>
                <div className="mt-2 hidden xl:flex items-center gap-2">
                  <ServiceCaseCategoryBadge category={serviceCase.category} />
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">Description</h2>
                <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-4">
                  {serviceCase.description || 'No description provided'}
                </div>
              </div>

              {/* Comments */}
              <div>
                <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <ChatBubbleLeftIcon className="h-5 w-5" />
                  Comments ({serviceCase.comments?.length || 0})
                </h2>

                {/* Comments list */}
                <div className="space-y-4 mb-6">
                  {serviceCase.comments?.map((comment) => (
                    <div
                      key={comment.id}
                      className={`rounded-lg p-4 ${
                        comment.isInternal
                          ? 'bg-amber-50 border border-amber-200'
                          : 'bg-gray-50 border border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar
                          firstName={comment.author.firstName ?? ''}
                          lastName={comment.author.lastName ?? ''}
                          size="sm"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {comment.author.firstName} {comment.author.lastName}
                            </span>
                            {comment.isInternal && (
                              <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                <LockClosedIcon className="h-3 w-3" />
                                Internal Note
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              {new Date(comment.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{comment.body}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!serviceCase.comments || serviceCase.comments.length === 0) && (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No comments yet
                    </p>
                  )}
                </div>

                {/* Add comment form */}
                <form onSubmit={handleCommentSubmit} className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">
                    Add Comment
                  </h3>
                  <textarea
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    rows={3}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                    placeholder="Write a comment..."
                  />
                  <div className="mt-3 flex items-center justify-between">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isInternal}
                        onChange={(e) => setIsInternal(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="text-sm text-gray-700 flex items-center gap-1">
                        <LockClosedIcon className="h-4 w-4" />
                        Mark as internal note
                      </span>
                    </label>
                    <button
                      type="submit"
                      disabled={!commentBody.trim() || addCommentMutation.isPending}
                      className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:opacity-50"
                    >
                      {addCommentMutation.isPending ? 'Posting...' : 'Post Comment'}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
